import React, { useState, useRef, useEffect } from "react";
import PlusIcon from "../../assets/images/plus-icon.svg";
import StarIcon from "../../assets/images/star-icon.svg";
import SendIcon from "../../assets/images/Submit.svg";
import ShowAssistantCardIcon from "../../assets/images/prompts-show-circle.svg";
import AiCheckIcon from "../../assets/images/ai-check.svg";
import AiCloseIcon from "../../assets/images/ai-close.svg";
import FilledCrossIcon from "../../assets/images/filled-cross-icon.svg";
import MediaIcon from "../../assets/images/media.svg";
import CameraIcon from "../../assets/images/camera.svg";
import MicrophoneIcon from "../../assets/images/microphone.svg";
import { withTranslation } from "react-i18next";
import useSpeechRecognition from "../hooks/useSpeechRecognition";
import useUploadService from "hooks/useUploadService";
import toast from "react-hot-toast";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Dropdown,
} from "reactstrap";
import Button from "components/Button";
import RemoveIcon from "../../assets/images/Remove-sm.svg";

const containerName = "ai-chat";

function ChatboxFooter({
  t,
  onSendMessage,
  setShowAssistantCard,
  showAssistantCard,
  messages,
}) {
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [uploadToggle, setUploadToggle] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const canceledRef = useRef(false);

  const { upload: uploadImage } = useUploadService();

  const {
    listening,
    transcript,
    error,
    startListening,
    stopListening,
    clearTranscript,
  } = useSpeechRecognition(t);

  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    // This code commeneted purposely
    // "image/webp",
    // "application/pdf",
    // "application/msword",
    // "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    // "application/vnd.ms-excel",
    // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    // "text/csv",
    // "application/csv",
  ];

  useEffect(() => {
    if (messages.length === 0) {
      setInput("");
      setSelectedFiles([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "26px";
      }
    }
  }, [messages]);

  useEffect(() => {
    if (
      transcript &&
      !isRecording &&
      transcript.trim() !== "" &&
      !canceledRef.current
    ) {
      setInput(transcript);

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${Math.min(
          textareaRef.current.scrollHeight,
          105
        )}px`;
      }
    }
  }, [transcript, isRecording]);

  useEffect(() => {
    setIsRecording(listening);

    if (listening) {
      setInput("");
    }
  }, [listening]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const handleFileChange = (event) => {
    if (!event || !event.target) return;

    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadToggle(false);

    if (selectedFiles.length + files.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    const validFiles = files.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Unsupported file format: ${file.name}`);
        return false;
      }
      return true;
    });

    const newFiles = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file: file,
      preview: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
      uploaded: false,
      filePath: null,
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);

    if (event.target) {
      event.target.value = "";
    }
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const handleStartRecording = async () => {
    if (error) {
      console.error(t("aiAssistant.onlineChat.recordingError"), error);
      return;
    }
    setInput("");
    canceledRef.current = false;
    await startListening();
  };

  const handleCancelRecording = async () => {
    canceledRef.current = true;
    await stopListening();
    await clearTranscript();
    setInput("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "26px";
    }
  };

  const handleConfirmRecording = async () => {
    await stopListening();
  };

  const uploadFilesToBlob = async () => {
    const filePaths = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileObj = selectedFiles[i];

      if (fileObj.file && !fileObj.uploaded) {
        try {
          const [err, blobData] = await uploadImage(
            fileObj.file,
            containerName
          );

          if (err) {
            throw new Error(err);
          }

          const filePath = `${containerName}/${blobData.blobName}`;
          filePaths.push(filePath);

          fileObj.uploaded = true;
          fileObj.filePath = filePath;
          fileObj.blobUrl = blobData.blobUrl;
        } catch (err) {
          toast.error(`Failed to upload ${fileObj.name}: ${err.message}`);
          throw err;
        }
      } else if (fileObj.uploaded && fileObj.filePath) {
        // File already uploaded, use existing path
        filePaths.push(fileObj.filePath);
      }
    }

    return filePaths;
  };

  const handleSend = async () => {
    if (input?.trim() === "" && selectedFiles.length === 0) return;

    // Validate that message is required when files are attached
    if (selectedFiles.length > 0 && input?.trim() === "") {
      toast.error(t("aiAssistant.onlineChat.messageRequiredWithImages"));
      return;
    }

    setIsUploading(true);

    try {
      let filePaths = [];

      if (selectedFiles.length > 0) {
        filePaths = await uploadFilesToBlob();
      }

      onSendMessage(input, filePaths, selectedFiles);

      selectedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });

      setInput("");
      setSelectedFiles([]);

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (err) {
      toast.error(t("Failed to send message"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleMediaClick = () => {
    fileInputRef.current?.click();
    setUploadToggle(false);
  };

  const handleCameraClick = async () => {
    try {
      setUploadToggle(false);
      setShowCameraModal(true);

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Use rear camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      setCameraStream(stream);

      // Set the video stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      toast.error(t("aiAssistant.onlineChat.unableToAccessCamera"));
      setShowCameraModal(false);
    }
  };

  const handleCapturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame to canvas
      context.drawImage(video, 0, 0);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a file from the blob
            const file = new File([blob], `camera-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });

            // Create a fake event to trigger file change
            const fakeEvent = {
              target: {
                files: [file],
              },
            };

            // Process the captured image
            handleFileChange(fakeEvent);
          }
        },
        "image/jpeg",
        0.8
      );

      handleCloseCamera();
    }
  };

  const handleCloseCamera = () => {
    // Stop the camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  return (
    <div className="chatbox-footer">
      <div className="left-icons">
        <Dropdown
          isOpen={uploadToggle}
          toggle={() => setUploadToggle(!uploadToggle)}
        >
          <DropdownToggle caret={false} tag="div" className="cursor-pointer">
            {uploadToggle ? (
              <img src={FilledCrossIcon} alt="Add" />
            ) : (
              <img src={PlusIcon} alt="Add" />
            )}
          </DropdownToggle>
          <DropdownMenu end className="office-dropdown-menu">
            <DropdownItem
              className="office-dropdown-item"
              onClick={handleMediaClick}
            >
              <img src={MediaIcon} alt="Media" className="mr-2" />
              {t("aiAssistant.onlineChat.mediaAndDocument")}
            </DropdownItem>
            <DropdownItem
              className="office-dropdown-item"
              onClick={handleCameraClick}
            >
              <img src={CameraIcon} alt="Camera" className="mr-2" />
              {t("aiAssistant.onlineChat.camera")}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          // This code commeneted purposely
          // accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: "none" }}
          multiple={false}
          webkitdirectory="false"
          data-camera="true"
          id="camera-input"
        />

        <button onClick={() => setShowAssistantCard(!showAssistantCard)}>
          {showAssistantCard ? (
            <img src={ShowAssistantCardIcon} alt="Show" />
          ) : (
            <img src={StarIcon} alt="Star" />
          )}
        </button>
      </div>

      <div className="input-container">
        <div
          className={`input-wrapper ${
            selectedFiles.length > 0 ? "has-attachments" : ""
          }`}
        >
          {/* File Previews inside input */}
          {selectedFiles.length > 0 && (
            <div className="file-previews-inline">
              {selectedFiles.map((file) => (
                <div key={file.id} className="file-preview-item-inline">
                  <button
                    onClick={() => handleRemoveFile(file.id)}
                    className="file-remove-btn-inline"
                    aria-label={t("aiAssistant.onlineChat.removeFile")}
                    disabled={isUploading}
                  >
                    <img src={RemoveIcon} alt="Remove" />
                  </button>
                  {file.type.startsWith("image/") ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="file-preview-image-inline"
                    />
                  ) : (
                    <div className="file-preview-document-inline">
                      <span className="file-name-inline">{file.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <textarea
            ref={textareaRef}
            className="message-input"
            name="message"
            placeholder={
              isRecording
                ? ""
                : selectedFiles.length > 0
                ? t("aiAssistant.onlineChat.typeMessageToSend")
                : "Message"
            }
            value={input}
            onChange={(e) => {
              const newValue = e.target.value;
              setInput(newValue);
              e.target.style.height = "auto";
              if (newValue.trim() === "") {
                e.target.style.height = "26px";
              } else {
                e.target.style.height = `${Math.min(
                  e.target.scrollHeight,
                  105
                )}px`;
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (
                  !isUploading &&
                  !isRecording &&
                  !(selectedFiles.length > 0 && input?.trim() === "") &&
                  !(input?.trim() === "" && selectedFiles.length === 0)
                ) {
                  handleSend();
                }
              }
            }}
            disabled={isRecording || isUploading}
          />
        </div>

        {isRecording && (
          <div className="recording-visualizer">
            <div className="waveform">
              {[...Array(40)].map((_, i) => (
                <div
                  key={i}
                  className="wave-bar"
                  style={{
                    height: `${Math.random() * 20 + 10}px`,
                    animationDelay: `${i * 0.03}s`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Icons inside the input box */}
        <div className="input-icons">
          {isRecording ? (
            <>
              <button
                className="cancel-btn"
                onClick={handleCancelRecording}
                title="Cancel recording"
              >
                <img src={AiCloseIcon} alt="Cancel" />
              </button>
              <button
                className="confirm-btn"
                onClick={handleConfirmRecording}
                title="Confirm recording"
              >
                <img src={AiCheckIcon} alt="Confirm" />
              </button>
            </>
          ) : (
            <button
              className="microphone-btn"
              onClick={handleStartRecording}
              title={
                error
                  ? t("aiAssistant.onlineChat.recognitionNotWorking")
                  : t("aiAssistant.onlineChat.startVoiveRecording")
              }
              disabled={isUploading}
            >
              <img src={MicrophoneIcon} alt="microphone" />
            </button>
          )}
        </div>
      </div>

      {error && <div className="speech-error">{error}</div>}

      {selectedFiles.length > 0 && input?.trim() === "" && !isRecording && (
        <div className="helper-text-message">
          {t("aiAssistant.onlineChat.pleaseTypeMessage")}
        </div>
      )}

      <button
        className="send-btn"
        onClick={handleSend}
        disabled={
          isUploading ||
          (selectedFiles.length > 0 && input?.trim() === "") ||
          (input?.trim() === "" && selectedFiles.length === 0)
        }
      >
        {isUploading ? <span>...</span> : <img src={SendIcon} alt="Send" />}
      </button>

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="camera-modal-overlay">
          <div className="camera-modal">
            <div className="camera-modal-header">
              <h3>{t("aiAssistant.onlineChat.takePhoto")}</h3>
              <button className="close-camera-btn" onClick={handleCloseCamera}>
                ×
              </button>
            </div>
            <div className="camera-modal-body">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
            <div className="common-appointment-btn-container">
              <Button
                className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
                onClick={handleCapturePhoto}
              >
                {t("aiAssistant.onlineChat.capture")}
              </Button>
              <Button
                className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
                onClick={handleCloseCamera}
              >
                {t("cancel")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default withTranslation()(ChatboxFooter);
